import React from 'react';
import { motion } from 'framer-motion';
import { Mail } from 'lucide-react';

// Define props interface for PressContact
interface PressContactProps {
  language?: string;
}

// Define translations for PressContact content
const TRANSLATIONS: Record<string, {
  title: string; // Base title without "Made in Germany" for consistent coloring
  subHeading: string;
  pressContacts: {
    name: string;
    role: string;
    email: string;
  }[];
  contactNote: string;
}> = {
  en: {
    title: "Press Contact",
    subHeading: "Connect with our dedicated press team for media inquiries, official statements, and comprehensive support tailored to journalists and industry professionals. Whether you're seeking detailed insights into our operations, exclusive interviews with our leadership, or resources to support your coverage, our team is here to assist with prompt, reliable, and expertly curated information.",
    pressContacts: [
      {
        name: "Andreas Thommen",
        role: "Chief Executive Officer",
        email: "press@made-in-germany.uk"
      },
      {
        name: "Sophie Müller",
        role: "Senior Press Officer",
        email: "media@made-in-germany.uk"
      },
      {
        name: "Liam Carter",
        role: "Media Relations Manager",
        email: "newsroom@made-in-germany.uk"
      }
    ],
    contactNote: "Our press team is committed to facilitating seamless communication for media representatives. For urgent inquiries or interview requests, please reach out via email to our press office. We strive to respond to all media-related correspondence within 24 hours, ensuring timely and accurate information aligned with our commitment to German precision and excellence."
  },
  de: {
    title: "Pressekontakt",
    subHeading: "Nehmen Sie Kontakt mit unserem engagierten Presseteam auf für Medienanfragen, offizielle Stellungnahmen und umfassende Unterstützung, speziell zugeschnitten auf Journalisten und Fachleute der Branche. Unser Team steht Ihnen zur Verfügung, um detaillierte Einblicke in unsere Prozesse zu geben, exklusive Interviews zu arrangieren und Ihnen alle notwendigen Ressourcen für Ihre Berichterstattung bereitzustellen.",
    pressContacts: [
      {
        name: "Andreas Thommen",
        role: "Geschäftsführer",
        email: "presse@made-in-germany.uk"
      },
      {
        name: "Sophie Müller",
        role: "Leitende Pressereferentin",
        email: "medien@made-in-germany.uk"
      },
      {
        name: "Liam Carter",
        role: "Manager für Medienbeziehungen",
        email: "nachrichtenraum@made-in-germany.uk"
      }
    ],
    contactNote: "Unser Presseteam setzt sich dafür ein, eine reibungslose Kommunikation für Medienvertreter zu gewährleisten. Bei dringenden Anfragen oder Interviewwünschen wenden Sie sich bitte per E-Mail an unser Pressebüro. Wir bemühen uns, alle medienbezogenen Anfragen innerhalb von 24 Stunden zu beantworten und dabei stets präzise und verlässliche Informationen im Sinne unserer deutschen Präzision und Exzellenz zu liefern."
  },
  es: {
    title: "Contacto de Prensa",
    subHeading: "Póngase en contacto con nuestro equipo de prensa especializado para consultas de medios, declaraciones oficiales y soporte integral diseñado para periodistas y profesionales del sector. Estamos aquí para ofrecerle información detallada sobre nuestras actividades, coordinar entrevistas exclusivas con nuestros líderes y proporcionarle todos los recursos necesarios para enriquecer su cobertura periodística con precisión y profesionalismo.",
    pressContacts: [
      {
        name: "Andreas Thommen",
        role: "Director Ejecutivo",
        email: "prensa@made-in-germany.uk"
      },
      {
        name: "Sophie Müller",
        role: "Oficial Senior de Prensa",
        email: "medios@made-in-germany.uk"
      },
      {
        name: "Liam Carter",
        role: "Gerente de Relaciones con los Medios",
        email: "sala-de-noticias@made-in-germany.uk"
      }
    ],
    contactNote: "Nuestro equipo de prensa está dedicado a facilitar una comunicación fluida para los representantes de los medios. Para consultas urgentes o solicitudes de entrevistas, contáctenos por correo electrónico a nuestra oficina de prensa. Nos esforzamos por responder a todas las solicitudes relacionadas con los medios en un plazo de 24 horas, garantizando información precisa y oportuna en línea con nuestro compromiso con la precisión y excelencia alemana."
  },
  fr: {
    title: "Contact Presse",
    subHeading: "Contactez notre équipe de presse dédiée pour des demandes de médias, des déclarations officielles et un soutien complet adapté aux journalistes et aux professionnels de l’industrie. Que vous ayez besoin d'informations approfondies sur nos activités, d’organiser des entretiens exclusifs avec nos dirigeants ou d’accéder à des ressources pour enrichir vos reportages, notre équipe est à votre disposition pour répondre avec efficacité et professionnalisme.",
    pressContacts: [
      {
        name: "Andreas Thommen",
        role: "Directeur Général",
        email: "presse@made-in-germany.uk"
      },
      {
        name: "Sophie Müller",
        role: "Attachée de Presse Senior",
        email: "medias@made-in-germany.uk"
      },
      {
        name: "Liam Carter",
        role: "Responsable des Relations Médias",
        email: "salle-de-presse@made-in-germany.uk"
      }
    ],
    contactNote: "Notre équipe de presse s’engage à offrir une communication fluide aux représentants des médias. Pour les demandes urgentes ou les requêtes d’interviews, veuillez nous contacter par e-mail à notre bureau de presse. Nous visons à répondre à toutes les correspondances liées aux médias dans les 24 heures, en fournissant des informations précises et rapides conformes à notre engagement envers la précision et l’excellence allemande."
  },
  it: {
    title: "Contatto Stampa",
    subHeading: "Contatta il nostro team stampa dedicato per richieste dei media, dichiarazioni ufficiali e supporto completo pensato per giornalisti e professionisti del settore. Siamo pronti a fornirti informazioni dettagliate sulle nostre operazioni, a organizzare interviste esclusive con i nostri leader e a offrirti tutte le risorse necessarie per supportare il tuo lavoro giornalistico con la massima accuratezza e attenzione ai dettagli.",
    pressContacts: [
      {
        name: "Andreas Thommen",
        role: "Amministratore Delegato",
        email: "stampa@made-in-germany.uk"
      },
      {
        name: "Sophie Müller",
        role: "Addetta Stampa Senior",
        email: "media@made-in-germany.uk"
      },
      {
        name: "Liam Carter",
        role: "Responsabile Relazioni con i Media",
        email: "sala-stampa@made-in-germany.uk"
      }
    ],
    contactNote: "Il nostro team stampa è impegnato a garantire una comunicazione senza intoppi per i rappresentanti dei media. Per richieste urgenti o richieste di interviste, contattateci via e-mail al nostro ufficio stampa. Ci impegniamo a rispondere a tutte le richieste relative ai media entro 24 ore, fornendo informazioni accurate e tempestive in linea con il nostro impegno per la precisione e l’eccellenza tedesca."
  },
  nl: {
    title: "Perscontact",
    subHeading: "Neem contact op met ons toegewijde persteam voor mediavragen, officiële verklaringen en uitgebreide ondersteuning afgestemd op journalisten en professionals uit de sector. Of u nu gedetailleerde informatie over onze activiteiten nodig heeft, exclusieve interviews met onze leidinggevenden wilt regelen, of toegang zoekt tot hulpmiddelen om uw verslaggeving te versterken, ons team staat klaar om u te helpen met snelle en betrouwbare service.",
    pressContacts: [
      {
        name: "Andreas Thommen",
        role: "Algemeen Directeur",
        email: "pers@made-in-germany.uk"
      },
      {
        name: "Sophie Müller",
        role: "Senior Persvoorlichter",
        email: "media@made-in-germany.uk"
      },
      {
        name: "Liam Carter",
        role: "Manager Mediabetrekkingen",
        email: "nieuwsruimte@made-in-germany.uk"
      }
    ],
    contactNote: "Ons persteam zet zich in voor een vlotte communicatie met mediavertegenwoordigers. Voor dringende vragen of interviewverzoeken kunt u ons per e-mail bereiken via ons perskantoor. Wij streven ernaar om binnen 24 uur te reageren op alle media-gerelateerde correspondentie, met nauwkeurige en tijdige informatie die onze toewijding aan Duitse precisie en excellentie weerspiegelt."
  },
  sa: {
    title: "اتصال الصحافة",
    subHeading: "تواصلوا مع فريقنا الصحفي المخصص للاستفسارات الإعلامية، والبيانات الرسمية، والدعم الشامل المصمم خصيصًا للصحفيين والمحترفين في الصناعة. سواء كنتم بحاجة إلى رؤى معمقة حول عملياتنا، أو ترتيب مقابلات حصرية مع قادتنا، أو الحصول على الموارد اللازمة لدعم تغطيتكم الإعلامية، فإن فريقنا موجود لتقديم المساعدة بسرعة ودقة عالية.",
    pressContacts: [
      {
        name: "أندرياس تومن",
        role: "الرئيس التنفيذي",
        email: "press@made-in-germany.uk"
      },
      {
        name: "صوفي مولر",
        role: "مسؤولة صحافة أولى",
        email: "media@made-in-germany.uk"
      },
      {
        name: "ليام كارتر",
        role: "مدير علاقات الإعلام",
        email: "newsroom@made-in-germany.uk"
      }
    ],
    contactNote: "فريقنا الصحفي ملتزم بتسهيل التواصل السلس لممثلي الإعلام. للاستفسارات العاجلة أو طلبات المقابلات، يرجى التواصل عبر البريد الإلكتروني مع مكتب الصحافة لدينا. نحن نسعى للرد على جميع الرسائل المتعلقة بالإعلام خلال 24 ساعة، مما يضمن تقديم معلومات دقيقة وفي الوقت المناسب تتماشى مع التزامنا بالدقة والتميز الألماني."
  },
  hk: {
    title: "新聞聯絡",
    subHeading: "聯繫我哋嘅專業新聞團隊，以進行媒體查詢、官方聲明同為記者同行業專業人士提供全面支援。無論您需要深入了解我哋嘅運作情況、安排同我哋領導層嘅獨家訪談，還是獲取支持您報導嘅資源，我哋嘅團隊都會喺度提供快速、可靠同專業嘅幫助。",
    pressContacts: [
      {
        name: "Andreas Thommen",
        role: "行政總裁",
        email: "press@made-in-germany.uk"
      },
      {
        name: "Sophie Müller",
        role: "高級新聞主任",
        email: "media@made-in-germany.uk"
      },
      {
        name: "Liam Carter",
        role: "媒體關係經理",
        email: "newsroom@made-in-germany.uk"
      }
    ],
    contactNote: "我哋嘅新聞團隊致力於為媒體代表提供順暢嘅溝通。對於緊急查詢或採訪請求，請透過電郵聯繫我哋嘅新聞辦公室。我哋目標喺24小時內回應所有同媒體相關嘅聯繫，確保提供準確同及時嘅資訊，體現我哋對德國精準同卓越嘅承諾。"
  },
  sg: {
    title: "新闻联络",
    subHeading: "联系我们的专业新闻团队，以进行媒体查询、官方声明以及为记者和行业专业人士提供全面支持。无论您是想深入了解我们的业务运作、安排与我们领导层的独家专访，还是需要资源来增强您的新闻报道，我们的团队都将为您提供迅速、可靠且精心准备的协助。",
    pressContacts: [
      {
        name: "Andreas Thommen",
        role: "首席执行官",
        email: "press@made-in-germany.uk"
      },
      {
        name: "Sophie Müller",
        role: "高级新闻官",
        email: "media@made-in-germany.uk"
      },
      {
        name: "Liam Carter",
        role: "媒体关系经理",
        email: "newsroom@made-in-germany.uk"
      }
    ],
    contactNote: "我们的新闻团队致力于为媒体代表提供顺畅的沟通。对于紧急查询或采访请求，请通过电子邮件联系我们的新闻办公室。我们目标在24小时内回应所有与媒体相关的联系，确保提供准确且及时的信息，体现我们对德国精准和卓越的承诺。"
  },
  za: {
    title: "Perskontak",
    subHeading: "Kontak ons toegewyde persspan vir mediavrae, amptelike verklarings en omvattende ondersteuning spesifiek vir joernaliste en professionele persone in die bedryf. Of jy nou gedetailleerde inligting oor ons bedrywighede soek, eksklusiewe onderhoude met ons leierskap wil reël, of hulpbronne benodig om jou verslaggewing te ondersteun, ons span is hier om jou met vinnige en betroubare diens by te staan.",
    pressContacts: [
      {
        name: "Andreas Thommen",
        role: "Uitvoerende Hoof",
        email: "pers@made-in-germany.uk"
      },
      {
        name: "Sophie Müller",
        role: "Senior Persbeampte",
        email: "media@made-in-germany.uk"
      },
      {
        name: "Liam Carter",
        role: "Bestuurder Mediaverhoudinge",
        email: "nuuskamer@made-in-germany.uk"
      }
    ],
    contactNote: "Ons persspan is toegewyd om gladde kommunikasie vir mediaverteenwoordigers te verseker. Vir dringende navrae of onderhoudsversoeke, kontak ons per e-pos by ons perskantoor. Ons streef daarna om binne 24 uur op alle media-verwante korrespondensie te reageer, met akkurate en tydige inligting wat ons verbintenis tot Duitse presisie en uitnemendheid weerspieël."
  },
  kr: {
    title: "언론 연락처",
    subHeading: "미디어 문의, 공식 성명 및 언론인과 업계 전문가를 위한 맞춤형 종합 지원을 위해 저희 전담 언론 팀에 연락하세요. 저희 운영에 대한 깊은 통찰, 리더십과의 독점 인터뷰安排, 또는 보도 자료를 강화하기 위한 리소스가 필요하시든, 저희 팀은 신속하고 신뢰할 수 있는 전문적인 도움을 제공하기 위해 준비되어 있습니다.",
    pressContacts: [
      {
        name: "안드레아스 톰멘",
        role: "최고경영자",
        email: "press@made-in-germany.uk"
      },
      {
        name: "소피 뮐러",
        role: "수석 언론 담당자",
        email: "media@made-in-germany.uk"
      },
      {
        name: "리암 카터",
        role: "미디어 관계 매니저",
        email: "newsroom@made-in-germany.uk"
      }
    ],
    contactNote: "저희 언론 팀은 미디어 대표들과 원활한 소통을 위해 최선을 다하고 있습니다. 긴급 문의나 인터뷰 요청의 경우, 이메일을 통해 저희 언론 사무실에 연락해 주세요. 모든 미디어 관련 연락에 24시간 내에 응답하여, 독일의 정밀함과 우수성에 대한 저희의 약속에 맞는 정확하고 시의적절한 정보를 제공하는 것을 목표로 합니다."
  },
  jp: {
    title: "報道関係連絡先",
    subHeading: "メディアのお問い合わせ、公式声明、記者や業界専門家向けに特化した包括的サポートのために、私たちの専任報道チームにご連絡ください。私たちの業務に関する詳細な情報が必要な場合、リーダーとの独占インタビューを調整したい場合、または報道をサポートするためのリソースをお求めの場合、私たちのチームは迅速かつ信頼性の高いプロフェッショナルな対応でお手伝いいたします。",
    pressContacts: [
      {
        name: "アンドレアス・トメン",
        role: "最高経営責任者",
        email: "press@made-in-germany.uk"
      },
      {
        name: "ソフィー・ミュラー",
        role: "上級報道担当者",
        email: "media@made-in-germany.uk"
      },
      {
        name: "リアム・カーター",
        role: "メディア関係マネージャー",
        email: "newsroom@made-in-germany.uk"
      }
    ],
    contactNote: "私たちの報道チームは、メディア代表とのスムーズなコミュニケーションを確保することに尽力しています。緊急のお問い合わせやインタビュー依頼については、メールにて報道事務局までご連絡ください。私たちはすべてのメディア関連の連絡に24時間以内に返信し、ドイツの精密さと卓越性への取り組みに沿った正確でタイムリーな情報提供を目指します。"
  }
};

const PressContact: React.FC<PressContactProps> = ({ language = 'en' }) => {
  const translations = TRANSLATIONS[language] || TRANSLATIONS['en'];

  // Define "Made in Germany" with German flag colors
  const madeInGermany = (
    <span>
      <span className="text-[#0B111F]">Made </span>
      <span className="text-[#d00b10]">in </span>
      <span className="text-[#ffcc00]">Germany</span>
    </span>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#FFFFFF] font-sans">
      {/* Blue Separator Line at the Top */}
      <div className="w-full h-1 bg-[#0B111F] mb-8" />

      <div className="max-w-7xl mx-auto px-0">
        {/* Main Content */}
        <main className="flex-grow container mx-auto px-6 py-16 flex flex-col justify-center items-center text-center">
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl md:text-6xl font-extrabold text-[#0B111F] mb-6 leading-tight">
              {translations.title}
              <br />
              {madeInGermany}
              <span className="text-[#0B111F]"> AG - UK Limited</span>
            </h2>
            <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              {translations.subHeading}
            </p>
          </motion.div>

          {/* Press Contacts */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="grid md:grid-cols-3 gap-8 mb-16 w-full max-w-5xl"
          >
            {translations.pressContacts.map((contact, index) => (
              <motion.div
                key={index}
                layout
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                whileHover={{ y: -10, boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)" }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                className="relative bg-[#0B111F] rounded-2xl p-6 border border-gray-800 backdrop-blur-xl overflow-hidden group flex flex-col items-center"
              >
                <div className="absolute inset-0 bg-[#0B111F] opacity-80" />
                <div className="relative z-10 flex flex-col space-y-4 items-center text-center">
                  <h3 className="text-xl font-bold text-white">{contact.name}</h3>
                  <p className="text-gray-200">{contact.role}</p>
                  <div className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-[#FFFFFF]" />
                    <a href={`mailto:${contact.email}`} className="text-gray-200 hover:text-[#d00b10] transition-colors">
                      {contact.email}
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Contact Note */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="relative bg-[#0B111F] rounded-2xl p-8 border border-gray-800 backdrop-blur-xl overflow-hidden max-w-3xl w-full"
          >
            <div className="absolute inset-0 bg-[#0B111F] opacity-80" />
            <div className="relative z-10">
              <p className="text-gray-200 text-center">{translations.contactNote}</p>
            </div>
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default PressContact;